import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Brain, ImagePlus, Music, Trash2, X } from 'lucide-react';

import type { CourseProfile, LearningStyleTag, VocalRangeTag } from '../../types/courseProfile';
import { EXAM_LEVEL_OPTIONS, type ExamLevelId } from '../../constants/examAiParams';
import {
  LEARNING_STYLE_OPTIONS,
  VOCAL_RANGE_OPTIONS,
  applyCourseSubjectIds,
  resolveCourseDisplayName,
  resolveCourseSubjectIds,
} from '../../constants/courseProfileUi';
import { SUBJECT_CATALOG } from '../../constants/subjectCatalog';
import { fileToSchoolLogoDataUrl, mapSchoolLogoError } from '../../lib/schoolLogo';

interface TagInputProps {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
}

function TagInput({ label, placeholder, values, onChange }: TagInputProps) {
  const [draft, setDraft] = useState('');

  const commitToken = (raw: string) => {
    const token = raw.trim().replace(/^[,;]+|[,;]+$/g, '');
    if (!token) return;
    if (values.some((value) => value.toLowerCase() === token.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...values, token]);
    setDraft('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitToken(draft);
    }
    if (event.key === 'Backspace' && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeTag = (index: number) => {
    onChange(values.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
        {label}
      </span>
      <div
        className="flex min-h-[42px] flex-wrap items-center gap-2 rounded-xl border px-2 py-2"
        style={{
          borderColor: 'var(--sala-border-strong)',
          background: 'var(--sala-surface)',
        }}
      >
        {values.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{
              background: 'color-mix(in srgb, var(--sala-accent) 18%, transparent)',
              color: 'var(--sala-ink)',
              border: '1px solid var(--sala-border-strong)',
            }}
          >
            {value}
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="rounded-full p-0.5 hover:bg-black/10"
              aria-label={`Quitar ${value}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => commitToken(draft)}
          placeholder={values.length === 0 ? placeholder : 'Añadir…'}
          className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--sala-ink-muted)]"
        />
      </div>
      <span className="mt-1 block text-[11px] text-[var(--sala-ink-muted)]">Enter o coma para añadir</span>
    </label>
  );
}

interface CourseProfileEditorProps {
  profile: CourseProfile;
  onChange: (profile: CourseProfile) => void;
  onSave: () => void;
  onDelete: () => void;
  isNew?: boolean;
  saving?: boolean;
}

export function CourseProfileEditor({
  profile,
  onChange,
  onSave,
  onDelete,
  isNew = false,
  saving = false,
}: CourseProfileEditorProps) {
  const [instrumentalEnabled, setInstrumentalEnabled] = useState(Boolean(profile.instrumentalProfile));
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInstrumentalEnabled(Boolean(profile.instrumentalProfile));
  }, [profile.id, profile.instrumentalProfile]);

  useEffect(() => {
    setLogoError(null);
  }, [profile.id]);

  const patch = useCallback(
    (partial: Partial<CourseProfile>) => {
      onChange({ ...profile, ...partial });
    },
    [onChange, profile],
  );

  const handleLogoPick = async (file: File | null) => {
    if (!file) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const dataUrl = await fileToSchoolLogoDataUrl(file);
      patch({ schoolLogoDataUrl: dataUrl });
    } catch (error) {
      const code = error instanceof Error ? error.message : 'school_logo_invalid';
      setLogoError(mapSchoolLogoError(code));
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const clearLogo = () => {
    patch({ schoolLogoDataUrl: undefined });
    setLogoError(null);
  };

  const toggleLearningStyle = (style: LearningStyleTag) => {
    const next = profile.learningStyle.includes(style)
      ? profile.learningStyle.filter((item) => item !== style)
      : [...profile.learningStyle, style];
    patch({ learningStyle: next });
  };

  const selectedSubjectIds = resolveCourseSubjectIds(profile);

  const toggleCourseSubject = (subjectId: string) => {
    if (selectedSubjectIds.includes(subjectId)) {
      if (selectedSubjectIds.length <= 1) return;
      onChange(applyCourseSubjectIds(profile, selectedSubjectIds.filter((id) => id !== subjectId)));
      return;
    }
    onChange(applyCourseSubjectIds(profile, [...selectedSubjectIds, subjectId]));
  };

  const setPrimarySubject = (subjectId: string) => {
    if (!selectedSubjectIds.includes(subjectId)) return;
    const rest = selectedSubjectIds.filter((id) => id !== subjectId);
    onChange(applyCourseSubjectIds(profile, [subjectId, ...rest]));
  };

  const handleInstrumentalToggle = (enabled: boolean) => {
    setInstrumentalEnabled(enabled);
    if (!enabled) {
      patch({ instrumentalProfile: undefined });
      return;
    }
    patch({
      instrumentalProfile: profile.instrumentalProfile ?? {
        instrumentsAvailable: [],
        vocalRange: 'children',
      },
    });
  };

  const instrumental = profile.instrumentalProfile;

  return (
    <div
      className="rounded-2xl border p-6"
      style={{
        borderColor: 'var(--sala-border-strong)',
        background: 'var(--sala-surface-raised)',
      }}
    >
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="sala-kicker mb-1">Course Persona</p>
          <h2 className="font-display text-2xl font-extrabold">
            {isNew ? 'Nuevo perfil de curso' : 'Editar perfil'}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-[var(--sala-ink-muted)]">
            Contexto agregado del grupo para personalizar tono, ejemplos y complejidad en la IA — sin lista de
            alumnos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew ? (
            <button type="button" className="sala-btn-outline text-xs text-red-400" onClick={onDelete}>
              <Trash2 className="mr-1 inline h-3.5 w-3.5" />
              Eliminar
            </button>
          ) : null}
          <button type="button" className="sala-btn-hero text-xs" onClick={onSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Colegio
          </span>
          <input
            className="sala-input-dark w-full"
            value={profile.schoolName ?? ''}
            onChange={(event) => patch({ schoolName: event.target.value })}
            placeholder="Ej: Colegio San Pedro"
          />
        </label>

        <div className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Logo del colegio
          </span>
          <div
            className="flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5"
            style={{
              borderColor: 'var(--sala-border-strong)',
              background: 'var(--sala-surface)',
            }}
          >
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border"
              style={{
                borderColor: 'var(--sala-border-strong)',
                background: '#fff',
              }}
              aria-hidden
            >
              {profile.schoolLogoDataUrl ? (
                <img
                  src={profile.schoolLogoDataUrl}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-lg font-extrabold text-[var(--sala-ink-muted)]">
                  {(profile.schoolName || 'E').trim().charAt(0).toUpperCase() || 'E'}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] leading-snug text-[var(--sala-ink-muted)]">
                Se muestra en la rúbrica impresa en lugar de la letra inicial. PNG, JPG o WebP.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
                  className="hidden"
                  onChange={(event) => void handleLogoPick(event.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  className="sala-btn-outline py-1 px-2.5 text-[11px]"
                  disabled={logoBusy}
                  onClick={() => logoInputRef.current?.click()}
                >
                  <ImagePlus className="mr-1 inline h-3.5 w-3.5" />
                  {logoBusy ? 'Procesando…' : profile.schoolLogoDataUrl ? 'Cambiar logo' : 'Subir logo'}
                </button>
                {profile.schoolLogoDataUrl ? (
                  <button
                    type="button"
                    className="sala-btn-outline py-1 px-2.5 text-[11px] text-red-400"
                    disabled={logoBusy}
                    onClick={clearLogo}
                  >
                    Quitar
                  </button>
                ) : null}
              </div>
              {logoError ? (
                <p className="mt-1.5 text-[11px] text-red-400">{logoError}</p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="block lg:col-span-2">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Asignaturas
          </span>
          <p className="mb-2 text-[11px] text-[var(--sala-ink-muted)]">
            Puedes marcar varias si trabajas todas las asignaturas en el mismo curso. La primera
            (o la marcada como principal) se usa por defecto en materiales monoasignatura.
          </p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Asignaturas del curso">
            {SUBJECT_CATALOG.filter((subject) => subject.id !== 'general').map((subject) => {
              const active = selectedSubjectIds.includes(subject.id);
              const isPrimary = profile.subjectId === subject.id;
              return (
                <button
                  key={subject.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleCourseSubject(subject.id)}
                  onDoubleClick={() => setPrimarySubject(subject.id)}
                  title={
                    active
                      ? isPrimary
                        ? 'Asignatura principal'
                        : 'Doble clic para hacer principal'
                      : `Añadir ${subject.name}`
                  }
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold transition"
                  style={{
                    borderColor: active
                      ? 'color-mix(in srgb, var(--sala-accent) 55%, var(--sala-border-strong))'
                      : 'var(--sala-border-strong)',
                    background: active
                      ? 'color-mix(in srgb, var(--sala-accent) 20%, transparent)'
                      : 'var(--sala-surface)',
                    color: 'var(--sala-ink)',
                  }}
                >
                  {subject.icon} {subject.name}
                  {isPrimary && active ? (
                    <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">principal</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block lg:col-span-2">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
            Nivel curricular
          </span>
          <select
            className="sala-input-dark w-full max-w-md"
            value={profile.level}
            onChange={(event) => {
              const level = event.target.value as ExamLevelId;
              patch({
                level,
                displayName: resolveCourseDisplayName({ level, displayName: '' }),
              });
            }}
          >
            <option value="">Selecciona un nivel…</option>
            {EXAM_LEVEL_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <Brain className="h-4 w-4 text-[var(--sala-accent-neon)]" strokeWidth={2.25} />
          <h3 className="font-display text-sm font-bold">Estilo de aprendizaje</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {LEARNING_STYLE_OPTIONS.map((option) => {
            const active = profile.learningStyle.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggleLearningStyle(option.id)}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold transition"
                style={{
                  borderColor: active ? 'var(--sala-accent)' : 'var(--sala-border-strong)',
                  background: active ? 'var(--sala-accent)' : 'transparent',
                  color: active ? 'var(--sala-on-accent, var(--sala-void))' : 'var(--sala-ink-muted)',
                }}
              >
                <span className="mr-1">{option.icon}</span>
                {option.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-8 grid gap-5 lg:grid-cols-1">
        <TagInput
          label="Intereses del curso"
          placeholder="fútbol, Minecraft, K-pop…"
          values={profile.interests}
          onChange={(interests) => patch({ interests })}
        />
        <TagInput
          label="Fortalezas del grupo"
          placeholder="buen ritmo, lectura fluida…"
          values={profile.strengths}
          onChange={(strengths) => patch({ strengths })}
        />
        <TagInput
          label="Dificultades a considerar"
          placeholder="fracciones, atención sostenida…"
          values={profile.difficulties}
          onChange={(difficulties) => patch({ difficulties })}
        />
      </section>

      <section
        className="mt-8 rounded-xl border p-4"
        style={{ borderColor: 'var(--sala-border-strong)', background: 'var(--sala-surface)' }}
      >
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={instrumentalEnabled}
            onChange={(event) => handleInstrumentalToggle(event.target.checked)}
            className="h-4 w-4 rounded accent-[var(--sala-accent)]"
          />
          <span className="flex items-center gap-2 font-display text-sm font-bold">
            <Music className="h-4 w-4 text-[var(--sala-accent-neon)]" strokeWidth={2.25} />
            Añadir Perfil Instrumental
          </span>
        </label>

        {instrumentalEnabled && instrumental ? (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <TagInput
              label="Instrumentos disponibles"
              placeholder="Flauta dulce, Metalófono…"
              values={instrumental.instrumentsAvailable}
              onChange={(instrumentsAvailable) =>
                patch({
                  instrumentalProfile: {
                    ...instrumental,
                    instrumentsAvailable,
                  },
                })
              }
            />
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--sala-ink-muted)]">
                Rango vocal
              </span>
              <select
                className="sala-input-dark w-full"
                value={instrumental.vocalRange ?? 'children'}
                onChange={(event) =>
                  patch({
                    instrumentalProfile: {
                      ...instrumental,
                      vocalRange: event.target.value as VocalRangeTag,
                    },
                  })
                }
              >
                {VOCAL_RANGE_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </section>
    </div>
  );
}
